import React, { useCallback, useState } from 'react';
import { gql, useQuery, useMutation } from '@common/hooks';
import {
  Button,
  ButtonGroup,
  Drawer,
  ErrorHandler,
  Form,
  Input,
} from '@common/components';
import { humanizeTimezone } from 'works/legacy/lib/date-helper';
import { Store } from 'antd/lib/form/interface';
import { Colors, FontSize, FontWeight } from '@common/tokens';
import { createMessage } from 'works/legacy/lib/message-helper';
import { HistoryInfoFragment } from '@common/graphql';
import {
  FormFieldUnitName,
  FormFieldClinicalUnit,
} from '@institutions/components';
import { ClinicalUnitType } from '@institutions/types';

const organizationUnitEditDrawerQuery = gql`
  query OrganizationUnitEditDrawerQuery($orgUnitId: ID!) {
    worksClinicalUnits {
      id
      displayName
    }
    organizationUnit(uuid: $orgUnitId) {
      id
      name
      costCenter
      clinicalUnit {
        id
        displayName
      }
      organizationBusiness {
        id
        name
        timeZone
        organizationUnits(input: { scope: "organization" }) {
          id
          name
        }
      }
    }
  }
`;

type OrganizationUnitEditDrawerQueryResponse = {
  worksClinicalUnits: ClinicalUnitType[];
  organizationUnit: {
    id: string;
    name: string;
    costCenter: string;
    clinicalUnit: {
      id: string;
      displayName: string;
    };
    organizationBusiness: {
      id: string;
      name: string;
      timeZone: string;
      organizationUnits: {
        id: string;
        name: string;
      }[];
    };
  };
};

type OrganizationUnitEditDrawerQueryVariables = {
  orgUnitId: string;
};

const organizationUnitEditDrawerMutation = gql`
  mutation OrganizationUnitEditDrawerMutation(
    $orgUnitId: ID!
    $input: UpdateOrganizationUnitInput!
  ) {
    updateOrganizationUnit(organizationUnitId: $orgUnitId, input: $input) {
      organizationUnit {
        id
        history {
          ...HistoryInfoFragment
        }
      }
    }
  }
  ${HistoryInfoFragment}
`;

type OrganizationUnitEditDrawerMutationVariables = {
  orgUnitId: string;
  input: {
    unitName: string;
    clinicalUnitId: string;
    costCenter?: string;
  };
};

type OrganizationUnitEditDrawerMutationResponse = {
  updateOrganizationUnit: {
    organizationUnit: {
      id: string;
    };
  };
};

const FIELDS_KEY = {
  UNIT_NAME: 'unitName',
  CLINICAL_UNIT: 'clinicalUnit',
  COST_CENTER: 'costCenter',
};

export function OrganizationUnitEditDrawer({
  open,
  onClose,
  organizationUnitId = '',
}: {
  open: boolean;
  onClose: () => void;
  organizationUnitId?: string;
}) {
  const [isSubmitButtonEnabled, setIsSubmitButtonEnabled] = useState(false);
  const [form] = Form.useForm();
  const cleanFormFields = form.resetFields;

  const closeDrawer = () => {
    resetFields(data);
    setIsSubmitButtonEnabled(false);
    onClose();
  };

  const resetFields = (
    givenData: OrganizationUnitEditDrawerQueryResponse | undefined
  ) => {
    form.setFieldsValue({
      [FIELDS_KEY.UNIT_NAME]: givenData?.organizationUnit.name,
      [FIELDS_KEY.CLINICAL_UNIT]: givenData?.organizationUnit.clinicalUnit?.id,
      [FIELDS_KEY.COST_CENTER]: givenData?.organizationUnit.costCenter,
    });
  };

  const errorToast = () =>
    createMessage('error', 'Something went wrong. Try again later.');

  const {
    data,
    error: queryError,
    loading: queryLoading,
    refetch,
  } = useQuery<
    OrganizationUnitEditDrawerQueryResponse,
    OrganizationUnitEditDrawerQueryVariables
  >(organizationUnitEditDrawerQuery, {
    skip: !organizationUnitId,
    variables: {
      orgUnitId: organizationUnitId,
    },
    onCompleted(response) {
      if (!response?.organizationUnit) {
        return;
      }

      resetFields(response);
    },
  });

  const updateSubmitButtonState = useCallback(() => {
    const dbData = [
      data?.organizationUnit.name,
      data?.organizationUnit.clinicalUnit?.id,
      data?.organizationUnit.costCenter,
    ];

    const formData = [
      form.getFieldValue(FIELDS_KEY.UNIT_NAME),
      form.getFieldValue(FIELDS_KEY.CLINICAL_UNIT),
      form.getFieldValue(FIELDS_KEY.COST_CENTER),
    ];

    const hasAnyDifferentData = dbData.some((dbDataValue, i) => {
      const formDataValue = formData[i];
      return dbDataValue !== formDataValue;
    });

    setIsSubmitButtonEnabled(hasAnyDifferentData);

    form.validateFields().catch((e) => {
      if (e?.errorFields?.length) {
        setIsSubmitButtonEnabled(false);
      }
    });
  }, [form, data]);

  const [save, { loading: mutationLoading }] = useMutation<
    OrganizationUnitEditDrawerMutationResponse,
    OrganizationUnitEditDrawerMutationVariables
  >(organizationUnitEditDrawerMutation, {
    onCompleted(response) {
      if (!response?.updateOrganizationUnit) {
        errorToast();
        return;
      }

      refetch();
      closeDrawer();
      cleanFormFields();
      createMessage('success', 'Unit details updated');
    },
    onError: errorToast,
  });

  if (queryError) {
    return <ErrorHandler error={queryError} log="OrganizationUnitEditDrawer" />;
  }

  function onFormFinish(payload: Store) {
    const handledFormData = {
      unitName: payload[FIELDS_KEY.UNIT_NAME].trim(),
      clinicalUnitId: payload[FIELDS_KEY.CLINICAL_UNIT],
      costCenter: payload[FIELDS_KEY.COST_CENTER].trim() || '',
    };

    save({
      variables: {
        orgUnitId: organizationUnitId,
        input: handledFormData,
      },
    });
  }

  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      title={data?.organizationUnit?.name}
      loading={queryLoading}
      footer={
        <ButtonGroup>
          <Button onClick={closeDrawer}>Cancel</Button>
          <Button
            kind="primary"
            onClick={form.submit}
            loading={mutationLoading}
            disabled={!isSubmitButtonEnabled}
          >
            Save
          </Button>
        </ButtonGroup>
      }
    >
      <div>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFormFinish}
          onValuesChange={updateSubmitButtonState}
        >
          <Form.Item label="Facility">
            <FacilityDetails
              organizationBusiness={
                data?.organizationUnit?.organizationBusiness
              }
            />
          </Form.Item>
          <FormFieldUnitName
            fieldName="unitName"
            currentOrganizationUnitId={organizationUnitId}
            organizationUnits={
              data?.organizationUnit?.organizationBusiness?.organizationUnits ||
              []
            }
          />
          <FormFieldClinicalUnit
            fieldName="clinicalUnit"
            clinicalUnits={data?.worksClinicalUnits || []}
          />
          <Form.Item label="Cost Center" name="costCenter">
            <Input placeholder="Type the Cost Center" />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
}

type FacilityDetailsType = {
  organizationBusiness?: {
    name: string;
    timeZone: string;
  };
};

function FacilityDetails({ organizationBusiness }: FacilityDetailsType) {
  return (
    <>
      <div
        style={{
          fontSize: FontSize.lg,
          fontWeight: FontWeight.medium,
        }}
      >
        {organizationBusiness?.name}
      </div>
      <div
        style={{
          fontSize: FontSize.sm,
          fontWeight: FontWeight.light,
          color: Colors.midnightRegular,
        }}
      >
        Time Zone: {humanizeTimezone(organizationBusiness?.timeZone)}
      </div>
    </>
  );
}
